import { useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { eventsBaseKey } from '@lib/cache/events';
import { currentUserKey } from '@lib/cache/users';
import { useLogtoAdapter } from '@lib/logto';
import { isNativePlatform } from '@lib/platform';
import { getStoredRedirectPath, clearRedirectPath } from '@utils/auth';
import { getCurrentUserProfile } from '@api-client/api-client';
import { readAuthIntent, readNativeAuthIntent, clearAuthIntent } from '@utils/authIntent';
import { clearRecoveryRecord } from '@utils/authRecovery';
import {
  executePostAuthIntent,
  type PostAuthIntentExecutionResult,
} from '@utils/executePostAuthIntent';
import { postIntent } from '@lib/overlay/intents';
import { useClaimPendingQuestions, settlePendingQuestionClaim } from './useClaimPendingQuestions';
import { logger } from '@utils/logger';

/**
 * Both attempts the two token blocks between them already allowed, now spent by
 * whichever step asks first so the CLAIM gets the retry too.
 */
const TOKEN_ATTEMPTS = 2;

function useLatest<T>(value: T) {
  const ref = useRef(value);

  useLayoutEffect(() => {
    ref.current = value;
  }, [value]);

  return ref;
}

/**
 * Native post-auth redirect hook.
 *
 * On web, signIn() triggers a page redirect to /callback, which runs post-auth
 * logic (new user detection, question claiming, redirect). On native, signIn()
 * resolves a Promise -- the fire-and-forget callers discard it. This hook watches
 * for isAuthenticated false->true transitions on native and runs the equivalent
 * post-auth redirect logic.
 */
export function useNativePostAuth() {
  const { isAuthenticated, getAccessToken } = useLogtoAdapter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { claimQuestions, invalidateQuestionsCache } = useClaimPendingQuestions();
  const isNative = isNativePlatform();
  const prevAuthRef = useRef(isAuthenticated);
  const navigateRef = useLatest(navigate);
  const queryClientRef = useLatest(queryClient);
  const getAccessTokenRef = useLatest(getAccessToken);
  const claimQuestionsRef = useLatest(claimQuestions);
  const invalidateQuestionsCacheRef = useLatest(invalidateQuestionsCache);

  useEffect(() => {
    if (!isNative) return;

    // Detect false -> true transition
    const justAuthenticated = isAuthenticated && !prevAuthRef.current;
    prevAuthRef.current = isAuthenticated;

    if (!justAuthenticated) return;

    async function executeNativePostAuth() {
      // Issue #567: Clear the shared recovery record now that auth succeeded so
      // a future invalid_grant starts a fresh attempt counter.
      clearRecoveryRecord();

      // 1. Invalidate events cache to fetch personalized data
      void queryClientRef.current.invalidateQueries({ queryKey: eventsBaseKey() });

      // 2. Check user profile via TanStack Query (warms currentUserKey() cache).
      // Uses customInstance which auto-injects JWT -- no manual token needed.
      try {
        await queryClientRef.current.fetchQuery({
          queryKey: currentUserKey(),
          queryFn: getCurrentUserProfile,
        });
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          navigateRef.current('/signup', { replace: true });
          return;
        }
        // Continue with normal flow on other errors -- TanStack Query will
        // retry the profile fetch when components that depend on it mount.
        logger.error('Native post-auth: failed to fetch user profile:', err);
      }

      // 3. Claim pending questions (needs explicit token for claimQuestions API)
      //
      // AIDEV-NOTE [MEETBALL magic-link]: this step FALLS THROUGH to step 4 and no
      // longer navigates, the native twin of the same fix in `useCallbackRedirect`. A
      // successful claim used to end in `navigate('/home')` plus a `return`, so a
      // sign-in carrying a guest question AND a pending action claimed the question
      // and dropped the action -- without executing it and without clearing it, and
      // on native the intent has a DURABLE copy (`readNativeAuthIntent`, 10-minute
      // `NATIVE_INTENT_TTL_MS`) that survives the app being killed, so it could fire
      // on a later sign-in that had nothing to do with it. A claim that arrives alone
      // still lands on `/home`: that is step 4's own default, and the ask cleared any
      // destination stashed before it at the create site (`useCreateQuestionSubmit`).
      //
      // ONE TOKEN FOR BOTH STEPS, which is the other half of making them a sequence.
      //
      // AIDEV-NOTE [MEETBALL magic-link]: step 3 fetched its own token and step 4
      // fetched another, so once step 3 stopped returning early, a token that
      // resolved for the claim and then failed for the intent (the SDK refreshing
      // between the two, a dropped connection on a mobile network) left step 4 with
      // `null` -- and `executeGoingEvent` logs "Missing access token" and returns the
      // fallback route, so the RSVP the visitor is being told about was dropped while
      // the question was claimed. The web callback has always used one token for both.
      //
      // ONE ACQUIRER rather than a fetch plus a copy of itself further down. That copy
      // was step 4's own retry, so a transient rejection gave the INTENT a second
      // chance and the CLAIM none -- the claim was skipped outright and its marker's
      // retry needs another sign-out/sign-in, which a now-signed-in user rarely
      // performs -- and its separate `logger.error` string reported one failure twice
      // at the only level a production build keeps. Lazy, so a sign-in carrying neither
      // a claim nor an intent fetches nothing; memoised, so a resolved token is fetched
      // once; and TWO attempts, which is what the two blocks already allowed, now shared
      // by both steps.
      //
      // THE ACQUIRER'S OWN LINE IS NOT A PRODUCTION RECORD, which is the other half of
      // removing the duplicate. `logger.error` is the only level a production build keeps,
      // so an `error` here plus the one the consumer writes when it gives up
      // (`settlePendingQuestionClaim`'s no-token line, `executePostAuthIntent`'s
      // missing-token line) was two records for one outage -- the double count the web
      // twin's rule at `useCallbackRedirect.ts` forbids. Nothing calls this acquirer
      // speculatively any more, so every token failure HAS a consumer that records what it
      // cost, and the rejection here is DEV context, which is what `logger.warn` is for.
      //
      // THAT DOES NOT MAKE A NATIVE OUTAGE ONE LINE (review 5330705557, R06). The adapter
      // underneath, `NativeLogtoAdapterProvider`'s `getAccessToken`, writes its own `error`
      // for every rejected attempt before it rethrows, so an outage with a claim pending
      // records THREE production lines (one per attempt, plus the settle's) and FOUR with a
      // token-needing intent (plus `executePostAuthIntent`'s). The adapter's line stays:
      // `AuthProvider` records its own token-fetch failures below `error`, so on native that
      // line is their only production record, and which layer owns a token failure is a
      // separate decision. The web adapter writes no line of its own, so on web the
      // consumer's is the whole record. `useNativePostAuth.tokenOutage.test.tsx` pins the
      // native count under the real adapter.
      let accessToken: string | null = null;
      let tokenAttempts = 0;
      const acquireAccessToken = async (): Promise<string | null> => {
        while (accessToken === null && tokenAttempts < TOKEN_ATTEMPTS) {
          tokenAttempts += 1;
          try {
            accessToken = await getAccessTokenRef.current(import.meta.env.VITE_LOGTO_RESOURCE);
          } catch (err) {
            if (tokenAttempts === 1) {
              logger.warn('Native post-auth: failed to get access token', err);
            }
          }
        }
        return accessToken;
      };

      // THE LADDER IS SHARED with the web callback now (`settlePendingQuestionClaim`,
      // beside the claim and the marker it settles). The two copies had to stay in
      // lockstep for `retryable` to mean one thing on both platforms and had already
      // stopped: web wrapped the claim in no `try`, so a rejection unwound its
      // `performRedirect` into `/home` with the intent neither executed nor cleared,
      // while this copy caught it, kept the marker and carried on to step 4. That
      // swallow is now the shared behaviour rather than this platform's.
      await settlePendingQuestionClaim({
        getAccessToken: acquireAccessToken,
        claimQuestions: claimQuestionsRef.current,
        invalidateQuestionsCache: invalidateQuestionsCacheRef.current,
        label: '[useNativePostAuth]',
      });

      // 4. Resolve auth intent centrally (mirrors useCallbackRedirect on web).
      // Try sessionStorage first; fall back to native Preferences for app-kill
      // scenarios where sessionStorage was lost but Preferences survived.
      const intent = readAuthIntent() ?? (await readNativeAuthIntent());
      const redirectPath = getStoredRedirectPath();

      let destination: PostAuthIntentExecutionResult = { path: redirectPath ?? '/home' };

      if (intent) {
        destination = await executePostAuthIntent({
          intent,
          redirectPath,
          queryClient: queryClientRef.current,
          // The token step 3 already resolved, or this step's second attempt at it --
          // the acquirer owns which, and asking for it here is what keeps the fetch off
          // a sign-in that carries neither a claim nor an intent.
          accessToken: await acquireAccessToken(),
        });
        logger.debug('[useNativePostAuth] Executed intent', {
          kind: intent.kind,
          path: destination.path,
        });
      }

      clearRedirectPath();
      clearAuthIntent();
      // This path is for an EXISTING user signing back in on-device, never a fresh signup, so no
      // 'signup' celebration is ever pending here — but a returning user's GOING_EVENT intent
      // still resolves a celebration draft, and it must still be posted immediately before
      // navigate(), or the RSVP celebration silently disappears for every returning native user
      // (see executePostAuthIntent).
      destination.intents?.forEach(draft => {
        postIntent(draft);
      });
      navigateRef.current(destination.path, { replace: true, state: destination.state });
    }

    void executeNativePostAuth().catch((err: unknown) => {
      logger.error('Native post-auth flow failed:', err);
      navigateRef.current('/home', { replace: true });
    });
  }, [
    isAuthenticated,
    isNative,
    claimQuestionsRef,
    getAccessTokenRef,
    invalidateQuestionsCacheRef,
    navigateRef,
    queryClientRef,
  ]);
}
